import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button, Row, Col, Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AiFillCheckCircle } from "react-icons/ai";
// api
import { paymentOkContest, markOrderPaid } from 'api/ecommerce';
//zustand stores
import { useUserStore } from 'stores/userStore';
import { useCartStore } from 'stores/cartStore';
import { formatCurrency } from 'utils/currencyFormatter';
// custom hooks
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const OrderConfirmation = () => {
  const { t } = useTranslation(['cart', 'header']);
  const { email, fetchUserInfo, isConcourseUser } = useUserStore();
  const { clear } = useCartStore();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState({});
  const query = useQuery();
  const navigate = useNavigate();
  const { assignedLibrary } = useAssignedLibrary();
  

  const hasExecuted = useRef(false);

  const generateOrder = useCallback(async () => {
    // Prevenir ejecuciones múltiples
    if (hasExecuted.current) {
      return;
    }
    hasExecuted.current = true;

    try {
      const dsMerchantParameters = query.get('Ds_MerchantParameters');
      const dsSignature = query.get('Ds_Signature');
      
      // Si es cliente concurso, usar paymentOkContest, sino usar paymentOk
      if (isConcourseUser) {
        const { data, status } = await paymentOkContest({});
        if (status === 200) {
          setInfo(data);
          await fetchUserInfo();
          clear();
          setLoading(false);
        } else if (status === 400) {
          navigate('/', { replace: true });
        }
      } else {
        // Usuario no concurso + TPV: usar mark-order-paid en vez de payment-ok
        const payload = {
          Ds_MerchantParameters: dsMerchantParameters,
          Ds_Signature: dsSignature,
        };
        const { data, status } = await markOrderPaid(payload);
        if (status === 200) {
          setInfo(data);
          await fetchUserInfo();
          clear();
          setLoading(false);
        } else if (status === 400) {
          navigate('/', { replace: true });
        }
      }
    } catch (error) {
      console.log(error);
      setInfo({});
      setLoading(true);
      hasExecuted.current = false; // Permitir reintento en caso de error
    }
  }, [query, isConcourseUser, fetchUserInfo, clear, navigate]);

  useEffect(() => {
    localStorage.removeItem('historyRedsys');
    generateOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <div className="loader-container">
        <Spinner animation="border" role="status" />
        <h3>{t('loadingOrder')}</h3>
      </div>
    );
  }

  return (
    <div className="order-confirmation-container">
      <div className="order-confirmation-card">
        <AiFillCheckCircle className="check-icon" />
        <h1 className='pedido-ok'>{t('successOrder')}</h1>
        <p className='my-4'>
          {t('orderNumber')} <span className="order-number">#{info.order_id}</span>
        </p>
        <p>{t('confirmationEmail')} <span className="email">{email}</span></p>
        <Row>
          <Col xs={6} md={6} className='my-2'>
            <div className="order-total">
              <p className='mb-1'>{t('orderTotal')}</p>
              <p>{formatCurrency(info.total_amount)}</p>
            </div>
          </Col>
          <Col xs={6} md={6} className='my-2'>
            <div className="payment-method">
              <p className='mb-1'>{t('paymentMethod')}</p>
              <p>{info.payment_method || ''}</p>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={6} className='mb-2'>
            <Link to={`/${assignedLibrary}/perfil`}>
              <Button className='my-account-button'>{t('myaccount', { ns: 'header' })}</Button>
            </Link>
          </Col>
          <Col md={6} className='mb-2'>
            <Link to="/">
              <Button className="continue-shopping-button">{t('keepBuying')}</Button>
            </Link>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default OrderConfirmation;
